import { useMemo, useState } from "react";
import type { RefObject } from "react";
import { Link } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ArrowRight, Check, X } from "lucide-react";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { z } from "zod";

import { Button } from "@/app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Input } from "@/app/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/app/components/ui/input-otp";
import { Label } from "@/app/components/ui/label";
import { Seo } from "../components/Seo";
import {
  CORPORATE_EMAIL_REQUIRED_MESSAGE,
  isCorporatePilotEmail,
  OPTIONAL_PHONE_PATTERN,
  WORK_EMAIL_FORMAT_MESSAGE,
  WORK_EMAIL_PATTERN,
} from "@/lib/formValidation";
import { useTurnstile } from "@/hooks/useTurnstile";
import { getPilotCountryOptions } from "@/lib/pilotCountryOptions";
import { adminLandingUrl, signupRequestUrl, signupVerifyUrl } from "@/lib/signupApi";

/** Marketing shell is dark without `.dark` on `html`, so theme tokens leave inputs borderless. Use explicit contrast here. */
const pilotInputClass =
  "h-10 rounded-lg border border-slate-500 bg-slate-950 text-slate-100 shadow-sm placeholder:text-slate-500 " +
  "focus-visible:border-teal-500 focus-visible:ring-2 focus-visible:ring-teal-500/25";

const pilotSelectClass =
  "h-10 w-full rounded-lg border border-slate-500 bg-slate-950 px-3 text-sm text-slate-100 shadow-sm " +
  "focus-visible:border-teal-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/25";

const pilotOtpSlotClass =
  "h-10 w-10 border-slate-500 bg-slate-950 text-slate-100 first:border-l focus-visible:border-teal-500 focus-visible:ring-teal-500/30";

const pilotLabelClass = "text-slate-200";

const MOBILE_FLAG =
  typeof import.meta.env.VITE_ENABLE_MOBILE_VERIFICATION === "string" &&
  import.meta.env.VITE_ENABLE_MOBILE_VERIFICATION.trim().toLowerCase() === "true";

const pilotFormSchema = z
  .object({
    contactPerson: z.string().trim().min(2, "Enter your full name.").max(200),
    companyName: z.string().trim().min(2, "Enter your company name.").max(200),
    email: z
      .string()
      .trim()
      .max(256)
      .refine((v) => WORK_EMAIL_PATTERN.test(v), WORK_EMAIL_FORMAT_MESSAGE),
    country: z.string().trim().min(2, "Select your country or region.").max(100),
    phone: z
      .string()
      .trim()
      .min(5, "Enter your phone number.")
      .max(50)
      .refine((v) => OPTIONAL_PHONE_PATTERN.test(v), "Use digits and common phone symbols only."),
    zipCode: z.string().trim().min(1, "Enter your ZIP or PIN code.").max(20),
  })
  .superRefine((data, ctx) => {
    if (!isCorporatePilotEmail(data.email)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CORPORATE_EMAIL_REQUIRED_MESSAGE,
        path: ["email"],
      });
    }
  });

type PilotFormValues = z.infer<typeof pilotFormSchema>;

type PilotSignupErrorBody = { message?: string; error?: string; status?: string };

/** Localhost / loopback: show operator-focused signup troubleshooting copy instead of public-safe text. */
function pilotSignupShowDevHints(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h.endsWith(".localhost");
}

async function parsePilotSignupJson(res: Response): Promise<PilotSignupErrorBody | null> {
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) return null;
  return (await res.json().catch(() => null)) as PilotSignupErrorBody | null;
}

function pilotSignupUserMessage(j: PilotSignupErrorBody | null, status: number, devHints: boolean): string {
  const err = typeof j?.error === "string" ? j.error : "";
  if (status === 503) {
    if (err.includes("SIGNUP_BACKEND") || err.toLowerCase().includes("not configured")) {
      if (devHints) {
        return "Pilot signup is not connected to the DeskReplay API. Run the backend server, apply migrations (backend/server: npm run db:migrate), and set SIGNUP_BACKEND_ORIGIN on the contact-api process. See website/.env.example.";
      }
      return "Pilot signup is temporarily unavailable. Please try again later or email sales@deskreplay.com.";
    }
    return "SMS verification is not available. Try again later or contact support.";
  }
  if (status === 502) {
    if (devHints) {
      return "Could not reach the signup service. Confirm the API is running and SIGNUP_BACKEND_ORIGIN matches its URL.";
    }
    return "Could not reach the signup service. Please try again shortly or email sales@deskreplay.com.";
  }
  if (status === 500) {
    if (devHints) {
      return "The signup service failed on the server. Run database migrations in backend/server (npm run db:migrate) and ensure email delivery is configured.";
    }
    return "The signup service failed. Please try again later or email sales@deskreplay.com.";
  }
  if (j?.message?.trim()) return j.message.trim();
  return "Something went wrong. Please try again.";
}

const PW_RULES = {
  minLen:    (v: string) => v.length >= 8,
  maxLen:    (v: string) => v.length <= 20,
  hasLower:  (v: string) => /[a-z]/.test(v),
  hasUpper:  (v: string) => /[A-Z]/.test(v),
  hasNumber: (v: string) => /[0-9]/.test(v),
  hasSymbol: (v: string) => /[^a-zA-Z0-9]/.test(v),
} as const;

const otpSchema = z
  .object({
    emailOtp: z.string().trim().min(6, "Enter the email code."),
    smsOtp: z.string().trim().optional(),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters.")
      .max(20, "Password must be at most 20 characters.")
      .regex(/[a-z]/, "Must include a lowercase letter.")
      .regex(/[A-Z]/, "Must include an uppercase letter.")
      .regex(/[0-9]/, "Must include a number.")
      .regex(/[^a-zA-Z0-9]/, "Must include a symbol (e.g. !@#$)."),
    confirmPassword: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Passwords do not match.", path: ["confirmPassword"] });
    }
    if (MOBILE_FLAG) {
      const s = (data.smsOtp ?? "").replace(/\s+/g, "");
      if (s.length < 6) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter the SMS verification code.",
          path: ["smsOtp"],
        });
      }
    }
  });

type OtpFormValues = z.infer<typeof otpSchema>;

function PilotBenefitsAside({ step }: { step: "form" | "otp" }) {
  const bulletsForm = [
    "Three endpoint licenses to see real sessions across your pilot fleet",
    "Seven-day rolling retention so you can review incidents during the pilot window",
    "Forensic-grade desktop capture you can export when disputes or audits surface",
    "Policy-controlled capture built for workforce trust, not stealth surveillance",
  ];

  if (step === "otp") {
    return (
      <aside className="self-start rounded-2xl border border-teal-500/25 bg-slate-900/50 p-6 lg:p-8 lg:sticky lg:top-28">
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-teal-400">Check your inbox</p>
        <h2 className="mb-3 text-xl font-bold text-slate-100">You are one step from your workspace</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          Enter the email code we sent, then set a strong password. After activation you will land in the admin console
          for your new organization.
        </p>
      </aside>
    );
  }

  return (
    <aside className="self-start lg:sticky lg:top-28">
      <div className="rounded-2xl border border-teal-500/25 bg-slate-900/50 p-6 lg:p-8">
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-teal-400">What you get in 7 days</p>
        <h2 className="mb-4 text-2xl font-bold leading-tight text-slate-100">
          A defensible view of what actually happened on the desk
        </h2>
        <p className="mb-6 text-sm leading-relaxed text-slate-400">
          DeskReplay turns desktop activity into evidence your compliance, security, and operations teams can stand behind:
          neutral capture, clear timelines, and exports when HR, legal, or regulators ask for proof.
        </p>
        <ul className="mb-8 space-y-3">
          {bulletsForm.map((text) => (
            <li key={text} className="flex gap-3 text-sm leading-relaxed text-slate-300">
              <Check className="mt-0.5 size-4 shrink-0 text-teal-500" aria-hidden />
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs leading-relaxed text-slate-500">
          Compare plans anytime on the{" "}
          <Link to="/pricing#essential" className="font-medium text-teal-400 hover:text-teal-300">
            pricing page
          </Link>{" "}
          or read how teams use session evidence on the{" "}
          <Link to="/features" className="font-medium text-teal-400 hover:text-teal-300">
            Features
          </Link>{" "}
          overview.
        </p>
      </div>
    </aside>
  );
}

const PW_RULE_LABELS: Array<{ key: keyof typeof PW_RULES; label: string }> = [
  { key: "minLen",    label: "8 to 20 characters" },
  { key: "hasLower",  label: "One lowercase letter" },
  { key: "hasUpper",  label: "One uppercase letter" },
  { key: "hasNumber", label: "One number" },
  { key: "hasSymbol", label: "One symbol (e.g. !@#$%^&*)" },
];

function PasswordStrengthChecklist({ value }: { value: string }) {
  return (
    <ul className="mt-2 space-y-1.5">
      {PW_RULE_LABELS.map(({ key, label }) => {
        const met = PW_RULES[key](value);
        return (
          <li
            key={key}
            className={`flex items-center gap-1.5 text-xs transition-colors ${
              met ? "text-teal-400" : "text-slate-500"
            }`}
          >
            {met ? (
              <Check className="size-3 shrink-0" aria-hidden />
            ) : (
              <X className="size-3 shrink-0" aria-hidden />
            )}
            {label}
          </li>
        );
      })}
    </ul>
  );
}

export function TrialSignupPage() {
  const [step, setStep] = useState<"form" | "otp">("form");
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingOtpAlreadySent, setPendingOtpAlreadySent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mobileOn = MOBILE_FLAG;
  const countryOptions = useMemo(() => getPilotCountryOptions(), []);

  const turnstileSiteKey = useMemo(() => {
    const v = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    return typeof v === "string" && v.trim() ? v.trim() : null;
  }, []);
  const { containerRef, turnstileReady, getToken, reset: resetTurnstile, loadError: turnstileLoadError } =
    useTurnstile(turnstileSiteKey);

  const form = useForm<PilotFormValues>({
    resolver: zodResolver(pilotFormSchema),
    defaultValues: {
      contactPerson: "",
      companyName: "",
      email: "",
      country: "",
      phone: "",
      zipCode: "",
    },
  });

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      emailOtp: "",
      smsOtp: "",
      password: "",
      confirmPassword: "",
    },
  });

  const watchedPassword = otpForm.watch("password");

  async function onPilotForm(values: PilotFormValues) {
    setSubmitError(null);
    setBusy(true);
    try {
      const turnstileToken = getToken();
      if (turnstileSiteKey && (!turnstileToken || turnstileToken.length === 0)) {
        setSubmitError("Complete the CAPTCHA below, then try again.");
        setBusy(false);
        return;
      }

      const url = signupRequestUrl();
      const payload: Record<string, string | undefined> = {
        email: values.email.trim().toLowerCase(),
        companyName: values.companyName.trim(),
        contactPerson: values.contactPerson.trim(),
        country: values.country.trim(),
        phone: values.phone.trim(),
        zipCode: values.zipCode.trim(),
      };
      if (turnstileToken) payload.turnstileToken = turnstileToken;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const j = await parsePilotSignupJson(res);

      if (res.status === 400) {
        if (turnstileSiteKey) resetTurnstile();
        setSubmitError(j?.message ?? "Could not start signup. Check your details.");
        return;
      }

      if (res.status === 503) {
        setSubmitError(pilotSignupUserMessage(j, res.status, pilotSignupShowDevHints()));
        return;
      }

      if (res.ok || res.status === 204) {
        setPendingEmail(values.email.trim().toLowerCase());
        setPendingOtpAlreadySent(j?.status === "otp_pending");
        otpForm.reset({
          emailOtp: "",
          smsOtp: "",
          password: "",
          confirmPassword: "",
        });
        setStep("otp");
        return;
      }

      if (turnstileSiteKey) resetTurnstile();
      setSubmitError(pilotSignupUserMessage(j, res.status, pilotSignupShowDevHints()));
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onOtpSubmit(values: OtpFormValues) {
    setSubmitError(null);
    setBusy(true);
    try {
      const verifyUrl = signupVerifyUrl();
      const body: Record<string, string> = {
        email: pendingEmail,
        emailOtp: values.emailOtp.replace(/\s+/g, ""),
        password: values.password,
      };
      if (mobileOn) {
        body.smsOtp = (values.smsOtp ?? "").replace(/\s+/g, "");
      }
      const res = await fetch(verifyUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const j = await parsePilotSignupJson(res);
        if (res.status === 503 || res.status === 502 || res.status === 500) {
          setSubmitError(pilotSignupUserMessage(j, res.status, pilotSignupShowDevHints()));
          return;
        }
        setSubmitError(j?.message ?? "Verification failed. Check your codes and password.");
        return;
      }

      window.location.assign(adminLandingUrl());
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo
        title="Start free pilot"
        description="Start a 7-day free pilot on DeskReplay managed SaaS. Evaluate policy-driven capture, evidence search, and exports with no credit card."
        canonicalPath="/get-started"
      />
      <div className="mx-auto max-w-6xl px-4 pb-12 pt-28 lg:pb-16">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-12 lg:items-start">
        <Card className="self-start rounded-2xl border border-slate-700 bg-slate-950 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-slate-50">
              {step === "form" ? "DeskReplay - Start your 7-day pilot for FREE" : "Verify and activate"}
            </CardTitle>
            {step === "form" ? (
              <p className="mt-2 inline-flex w-fit max-w-full items-center rounded-full border border-teal-500/40 bg-teal-500/15 px-3 py-1 text-xs font-semibold tracking-wide text-teal-100">
                No credit card required
              </p>
            ) : null}
            <CardDescription className={step === "form" ? "mt-3 text-slate-400" : "text-slate-400"}>
              {step === "form"
                ? "Corporate email required. We will send a verification code to activate your DeskReplay pilot workspace."
                : `Enter the code sent to ${pendingEmail}, choose a password, then continue to the admin console.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === "form" ? (
              <form className="flex flex-col gap-4" noValidate onSubmit={form.handleSubmit(onPilotForm)}>
                <div className="space-y-2">
                  <Label htmlFor="fullName" className={pilotLabelClass}>
                    Full name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    autoComplete="name"
                    className={pilotInputClass}
                    {...form.register("contactPerson")}
                  />
                  {form.formState.errors.contactPerson ? (
                    <p className="text-sm text-red-400">{form.formState.errors.contactPerson.message}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className={pilotLabelClass}>
                    Company name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="company"
                    autoComplete="organization"
                    className={pilotInputClass}
                    {...form.register("companyName")}
                  />
                  {form.formState.errors.companyName ? (
                    <p className="text-sm text-red-400">{form.formState.errors.companyName.message}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className={pilotLabelClass}>
                    Work email <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={pilotInputClass}
                    {...form.register("email")}
                  />
                  {form.formState.errors.email ? (
                    <p className="text-sm text-red-400">{form.formState.errors.email.message}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className={pilotLabelClass}>
                    Phone number <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    className={pilotInputClass}
                    placeholder="+1 555 000 0000"
                    {...form.register("phone")}
                  />
                  {form.formState.errors.phone ? (
                    <p className="text-sm text-red-400">{form.formState.errors.phone.message}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country" className={pilotLabelClass}>
                    Country / region <span className="text-red-400">*</span>
                  </Label>
                  <select id="country" autoComplete="country" className={pilotSelectClass} {...form.register("country")}>
                    <option value="" disabled>
                      Select country / region
                    </option>
                    {countryOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {form.formState.errors.country ? (
                    <p className="text-sm text-red-400">{form.formState.errors.country.message}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zipCode" className={pilotLabelClass}>
                    ZIP / PIN code <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="zipCode"
                    autoComplete="postal-code"
                    className={pilotInputClass}
                    placeholder="e.g. 10001 or 110001"
                    {...form.register("zipCode")}
                  />
                  {form.formState.errors.zipCode ? (
                    <p className="text-sm text-red-400">{form.formState.errors.zipCode.message}</p>
                  ) : null}
                </div>

                {turnstileSiteKey ? (
                  <div className="flex flex-col gap-2">
                    <span className={pilotLabelClass}>CAPTCHA</span>
                    <div
                      ref={containerRef as RefObject<HTMLDivElement>}
                      className="flex min-h-[65px] w-full justify-center rounded-lg border border-slate-600 bg-slate-950/50 py-2"
                    />
                    {turnstileLoadError ? (
                      <p className="text-sm leading-snug text-amber-400">{turnstileLoadError}</p>
                    ) : null}
                  </div>
                ) : null}

                {submitError ? <p className="text-sm text-red-400">{submitError}</p> : null}

                <Button
                  type="submit"
                  className="w-full gap-2 bg-teal-600 text-white hover:bg-teal-500 focus-visible:ring-teal-500/40"
                  disabled={busy || (!!turnstileSiteKey && !turnstileReady)}
                >
                  {busy ? "Sending codes..." : "Continue"}
                  <ArrowRight className="size-4" />
                </Button>
              </form>
            ) : (
              <form className="flex flex-col gap-6" noValidate onSubmit={otpForm.handleSubmit(onOtpSubmit)}>
                {pendingOtpAlreadySent && (
                  <p className="text-sm text-teal-300 border border-teal-500/30 rounded-lg px-3 py-2 bg-teal-500/10">
                    We already sent a verification code to this address. Please check your inbox.
                  </p>
                )}
                <div className="space-y-2">
                  <Label className={pilotLabelClass}>Email verification code <span className="text-red-400">*</span></Label>
                  <InputOTP
                    maxLength={6}
                    pattern={REGEXP_ONLY_DIGITS}
                    value={otpForm.watch("emailOtp")}
                    onChange={(v) => otpForm.setValue("emailOtp", v)}
                    containerClassName="justify-start"
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} className={pilotOtpSlotClass} />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                  {otpForm.formState.errors.emailOtp ? (
                    <p className="text-sm text-red-400">{otpForm.formState.errors.emailOtp.message}</p>
                  ) : null}
                </div>

                {mobileOn ? (
                  <div className="space-y-2">
                    <Label className={pilotLabelClass}>SMS verification code</Label>
                    <InputOTP
                      maxLength={6}
                      pattern={REGEXP_ONLY_DIGITS}
                      value={otpForm.watch("smsOtp") ?? ""}
                      onChange={(v) => otpForm.setValue("smsOtp", v)}
                      containerClassName="justify-start"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className={pilotOtpSlotClass} />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                    {otpForm.formState.errors.smsOtp ? (
                      <p className="text-sm text-red-400">{otpForm.formState.errors.smsOtp.message}</p>
                    ) : null}
                  </div>
                ) : null}

                <div className="space-y-2">
                  <Label htmlFor="pw" className={pilotLabelClass}>
                    Password <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="pw"
                    type="password"
                    autoComplete="new-password"
                    className={pilotInputClass}
                    {...otpForm.register("password")}
                  />
                  {otpForm.formState.errors.password ? (
                    <p className="text-sm text-red-400">{otpForm.formState.errors.password.message}</p>
                  ) : null}
                  <PasswordStrengthChecklist value={watchedPassword} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pw2" className={pilotLabelClass}>
                    Confirm password <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="pw2"
                    type="password"
                    autoComplete="new-password"
                    className={pilotInputClass}
                    {...otpForm.register("confirmPassword")}
                  />
                  {otpForm.formState.errors.confirmPassword ? (
                    <p className="text-sm text-red-400">{otpForm.formState.errors.confirmPassword.message}</p>
                  ) : null}
                </div>

                {submitError ? <p className="text-sm text-red-400">{submitError}</p> : null}

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 border-slate-500 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-slate-100"
                    disabled={busy}
                    onClick={() => setStep("form")}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-teal-600 text-white hover:bg-teal-500 focus-visible:ring-teal-500/40"
                    disabled={busy}
                  >
                    {busy ? "Activating..." : "Activate pilot workspace"}
                  </Button>
                </div>
              </form>
            )}

            <p className="mt-6 text-center text-sm text-slate-500 lg:text-left">
              <Link
                to="/pricing#essential"
                className="text-slate-400 underline-offset-4 hover:underline"
              >
                Back to pricing
              </Link>
            </p>
          </CardContent>
        </Card>

          <PilotBenefitsAside step={step} />
        </div>
      </div>
    </>
  );
}
